import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteOutlineIcon,
  ExpandMore as ExpandMoreIcon,
  LocalOfferOutlined as LocalOfferOutlinedIcon,
} from '@mui/icons-material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { adminApiClient } from '../../adminApiClient';
import { Organization } from '../../models/Organization';
import {
  OrganizationSubscriptionPlanOffer,
  OrganizationSubscriptionPlanSubscriber,
  SubscriptionPlanCode,
  SubscriptionPlanSummary,
  SubscriptionStatus,
} from '../../models/Subscription';
import { formatPlanPrice } from '../../components/subscription/subscriptionPlanUi';

type CatalogueTab = 'private' | 'standard';

function formatOfferDate(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    return format(parseISO(value), 'MMM d, yyyy HH:mm');
  } catch {
    return value;
  }
}

function sortPlans(plans: SubscriptionPlanSummary[]): SubscriptionPlanSummary[] {
  return [...plans].sort((a, b) => {
    if (a.organizationType !== b.organizationType) {
      return a.organizationType.localeCompare(b.organizationType);
    }
    return a.code.localeCompare(b.code);
  });
}

/**
 * Admin Plans page: private + standard catalogues with expandable recipients and a Make offer dialog.
 */
const AdminSubscriptionPlansPage: React.FC = () => {
  const { t } = useTranslation();

  const [catalogueTab, setCatalogueTab] = useState<CatalogueTab>('private');
  const [privatePlans, setPrivatePlans] = useState<SubscriptionPlanSummary[]>([]);
  const [standardPlans, setStandardPlans] = useState<SubscriptionPlanSummary[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [offersByPriceId, setOffersByPriceId] = useState<
    Record<string, OrganizationSubscriptionPlanOffer[]>
  >({});
  const [subscribersByPriceId, setSubscribersByPriceId] = useState<
    Record<string, OrganizationSubscriptionPlanSubscriber[]>
  >({});
  const [expandedPriceId, setExpandedPriceId] = useState<string | false>(false);
  const [loadingOfferPriceIds, setLoadingOfferPriceIds] = useState<Record<string, boolean>>({});
  const [loadingSubscriberPriceIds, setLoadingSubscriberPriceIds] = useState<
    Record<string, boolean>
  >({});

  const [loadingPlans, setLoadingPlans] = useState(true);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [revokingKey, setRevokingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogPlanPriceId, setDialogPlanPriceId] = useState('');
  const [dialogOrg, setDialogOrg] = useState<Organization | null>(null);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [note, setNote] = useState('');

  const cataloguePlans = catalogueTab === 'private' ? privatePlans : standardPlans;

  const planCodeLabel = (code: SubscriptionPlanCode | string) =>
    t(`subscription.planCodes.${code}`, String(code).replace(/_/g, ' '));

  const orgTypeLabel = (type: string) =>
    t(`subscription.orgTypes.${type}`, type.replace(/_/g, ' '));

  const statusLabel = (status: SubscriptionStatus | string) =>
    t(`subscription.statuses.${status}`, String(status).replace(/_/g, ' '));

  const loadOffersForPrice = useCallback(
    async (priceId: string) => {
      try {
        setLoadingOfferPriceIds((prev) => ({ ...prev, [priceId]: true }));
        const data = await adminApiClient.listSubscriptionPlanOffersByPriceId(priceId);
        setOffersByPriceId((prev) => ({
          ...prev,
          [priceId]: Array.isArray(data) ? data : [],
        }));
      } catch (err) {
        console.error(`Error loading offers for price ${priceId}:`, err);
        setError(
          t('adminPlans.fetchRecipientsError', 'Failed to load organizations for plans'),
        );
        setOffersByPriceId((prev) => ({ ...prev, [priceId]: prev[priceId] || [] }));
      } finally {
        setLoadingOfferPriceIds((prev) => ({ ...prev, [priceId]: false }));
      }
    },
    [t],
  );

  const loadSubscribersForPrice = useCallback(
    async (priceId: string) => {
      try {
        setLoadingSubscriberPriceIds((prev) => ({ ...prev, [priceId]: true }));
        const data = await adminApiClient.listSubscriptionPlanSubscribersByPriceId(priceId);
        setSubscribersByPriceId((prev) => ({
          ...prev,
          [priceId]: Array.isArray(data) ? data : [],
        }));
      } catch (err) {
        console.error(`Error loading subscribers for price ${priceId}:`, err);
        setError(
          t(
            'adminPlans.fetchSubscribersError',
            'Failed to load organizations subscribed to plans',
          ),
        );
        setSubscribersByPriceId((prev) => ({ ...prev, [priceId]: prev[priceId] || [] }));
      } finally {
        setLoadingSubscriberPriceIds((prev) => ({ ...prev, [priceId]: false }));
      }
    },
    [t],
  );

  const loadPlanDetails = useCallback(
    async (priceId: string, tab: CatalogueTab) => {
      if (tab === 'private') {
        await loadOffersForPrice(priceId);
      } else {
        await loadSubscribersForPrice(priceId);
      }
    },
    [loadOffersForPrice, loadSubscribersForPrice],
  );

  const loadCatalogue = useCallback(
    async (tab: CatalogueTab) => {
      try {
        setLoadingPlans(true);
        setError(null);
        const data =
          tab === 'private'
            ? await adminApiClient.getPrivateSubscriptionPlans()
            : await adminApiClient.getSelfServiceSubscriptionPlans();
        const plans = sortPlans(Array.isArray(data) ? data : []);
        if (tab === 'private') {
          setPrivatePlans(plans);
        } else {
          setStandardPlans(plans);
        }
        await Promise.all(plans.map((plan) => loadPlanDetails(plan.priceId, tab)));
      } catch (err) {
        console.error(`Error loading ${tab} subscription plans:`, err);
        setError(
          tab === 'private'
            ? t('adminPlans.fetchPlansError', 'Failed to load private subscription plans')
            : t(
                'adminPlans.fetchStandardPlansError',
                'Failed to load standard subscription plans',
              ),
        );
        if (tab === 'private') {
          setPrivatePlans([]);
        } else {
          setStandardPlans([]);
        }
      } finally {
        setLoadingPlans(false);
      }
    },
    [loadPlanDetails, t],
  );

  const loadOrganizations = useCallback(async () => {
    try {
      setLoadingOrgs(true);
      const data = await adminApiClient.getOrganizations();
      const orgs = (Array.isArray(data) ? data : [])
        .filter((org) => org && org.name !== 'Band Space Org' && org.id !== 'Band Space Org')
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setOrganizations(orgs);
    } catch (err) {
      console.error('Error loading organizations for plan offers:', err);
      setError(t('organizations.fetchError', 'Failed to fetch organizations'));
      setOrganizations([]);
    } finally {
      setLoadingOrgs(false);
    }
  }, [t]);

  useEffect(() => {
    void loadCatalogue(catalogueTab);
  }, [catalogueTab, loadCatalogue]);

  useEffect(() => {
    void loadOrganizations();
  }, [loadOrganizations]);

  const openNewOfferDialog = (preselectedPriceId?: string) => {
    setDialogPlanPriceId(preselectedPriceId || privatePlans[0]?.priceId || '');
    setDialogOrg(null);
    setExpiresAt(null);
    setNote('');
    setError(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (submitting) return;
    setDialogOpen(false);
  };

  const handleGrant = async () => {
    if (!dialogOrg?.id) {
      setError(t('adminPlans.orgRequired', 'Select an organization'));
      return;
    }
    if (!dialogPlanPriceId) {
      setError(t('organizations.planOffers.planRequired', 'Select a subscription plan'));
      return;
    }
    if (expiresAt) {
      if (Number.isNaN(expiresAt.getTime()) || expiresAt.getTime() <= Date.now()) {
        setError(
          t(
            'organizations.planOffers.expiresAtInvalid',
            'Expiry must be a future date and time',
          ),
        );
        return;
      }
    }

    try {
      setSubmitting(true);
      setError(null);
      await adminApiClient.grantSubscriptionPlanOffer(dialogOrg.id, {
        subscriptionPlanPriceId: dialogPlanPriceId,
        expiresAt: expiresAt ? expiresAt.toISOString() : null,
        note: note.trim() ? note.trim() : null,
      });
      setDialogOpen(false);
      await loadOffersForPrice(dialogPlanPriceId);
      setExpandedPriceId(dialogPlanPriceId);
    } catch (err) {
      console.error('Error granting subscription plan offer:', err);
      setError(
        t(
          'organizations.planOffers.grantError',
          'Failed to grant subscription plan offer',
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (organizationId: string, subscriptionPlanPriceId: string) => {
    const key = `${organizationId}:${subscriptionPlanPriceId}`;
    try {
      setRevokingKey(key);
      setError(null);
      await adminApiClient.revokeSubscriptionPlanOffer(organizationId, subscriptionPlanPriceId);
      setOffersByPriceId((prev) => ({
        ...prev,
        [subscriptionPlanPriceId]: (prev[subscriptionPlanPriceId] || []).filter(
          (offer) => offer.organizationId !== organizationId,
        ),
      }));
    } catch (err) {
      console.error('Error revoking subscription plan offer:', err);
      setError(
        t(
          'organizations.planOffers.revokeError',
          'Failed to revoke subscription plan offer',
        ),
      );
    } finally {
      setRevokingKey(null);
    }
  };

  const handleAccordionChange =
    (priceId: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedPriceId(isExpanded ? priceId : false);
      if (!isExpanded) return;
      const needsData =
        catalogueTab === 'private'
          ? offersByPriceId[priceId] == null
          : subscribersByPriceId[priceId] == null;
      if (needsData) {
        void loadPlanDetails(priceId, catalogueTab);
      }
    };

  const handleCatalogueTabChange = (
    _event: React.SyntheticEvent,
    value: CatalogueTab,
  ) => {
    setCatalogueTab(value);
    setExpandedPriceId(false);
    setError(null);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, pb: { xs: 8, md: 10 }, maxWidth: 1100, width: '100%', mx: 'auto' }}>
      <Card elevation={3} sx={{ p: { xs: 2, md: 3 } }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ sm: 'flex-start' }}
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Box>
            <Typography variant="h4" component="h1">
              {t('adminPlans.title', 'Plans')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              {catalogueTab === 'private'
                ? t(
                    'adminPlans.subtitlePrivate',
                    'Private catalogue. Expand a plan to manage organization offers.',
                  )
                : t(
                    'adminPlans.subtitleStandard',
                    'Standard self-service catalogue. Expand a plan to see subscribed organizations.',
                  )}
            </Typography>
          </Box>
          {catalogueTab === 'private' ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => openNewOfferDialog()}
              disabled={loadingPlans || privatePlans.length === 0}
            >
              {t('adminPlans.newOffer', 'Make offer')}
            </Button>
          ) : null}
        </Stack>

        <Tabs
          value={catalogueTab}
          onChange={handleCatalogueTabChange}
          sx={{ mt: 1, borderBottom: 1, borderColor: 'divider' }}
          aria-label={t('adminPlans.catalogueTabs', 'Plan catalogues')}
        >
          <Tab
            value="private"
            label={t('adminPlans.tabPrivate', 'Private')}
            id="admin-plans-tab-private"
            aria-controls="admin-plans-panel-private"
          />
          <Tab
            value="standard"
            label={t('adminPlans.tabStandard', 'Standard')}
            id="admin-plans-tab-standard"
            aria-controls="admin-plans-panel-standard"
          />
        </Tabs>

        {error ? (
          <Alert severity="error" sx={{ mt: 2, mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}

        <Box
          role="tabpanel"
          sx={{ mt: 2 }}
          id={
            catalogueTab === 'private'
              ? 'admin-plans-panel-private'
              : 'admin-plans-panel-standard'
          }
          aria-labelledby={
            catalogueTab === 'private'
              ? 'admin-plans-tab-private'
              : 'admin-plans-tab-standard'
          }
        >
          {loadingPlans ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : cataloguePlans.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {catalogueTab === 'private'
                ? t(
                    'organizations.planOffers.noPrivatePlans',
                    'No private subscription plans are available',
                  )
                : t(
                    'adminPlans.noStandardPlans',
                    'No standard subscription plans are available',
                  )}
            </Typography>
          ) : (
            <Stack spacing={1}>
              {cataloguePlans.map((plan) => {
                const offers = offersByPriceId[plan.priceId];
                const subscribers = subscribersByPriceId[plan.priceId];
                const loadingOffers = Boolean(loadingOfferPriceIds[plan.priceId]);
                const loadingSubscribers = Boolean(loadingSubscriberPriceIds[plan.priceId]);
                const subscriberCount = subscribers == null ? null : subscribers.length;
                const offerCount = offers == null ? null : offers.length;
                return (
                  <Accordion
                    key={plan.priceId}
                    disableGutters
                    elevation={0}
                    expanded={expandedPriceId === plan.priceId}
                    onChange={handleAccordionChange(plan.priceId)}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: '12px !important',
                      '&:before': { display: 'none' },
                      overflow: 'hidden',
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        '& .MuiAccordionSummary-content': {
                          my: 1.25,
                          alignItems: 'center',
                          gap: 1,
                        },
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ width: '100%', pr: 0.5, minWidth: 0 }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ minWidth: 0, flex: 1 }}
                        >
                          {catalogueTab === 'private' ? (
                            <IconButton
                              size="small"
                              color="primary"
                              aria-label={t('adminPlans.newOfferForPlan', 'Offer')}
                              onClick={(event) => {
                                event.stopPropagation();
                                openNewOfferDialog(plan.priceId);
                              }}
                              sx={{ flexShrink: 0 }}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          ) : null}
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="subtitle1" fontWeight={700} noWrap>
                              {planCodeLabel(plan.code)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" noWrap>
                              {orgTypeLabel(plan.organizationType)} ·{' '}
                              {formatPlanPrice(plan.amount, plan.currencyCode)} ·{' '}
                              {plan.onlineBookingCommissionPercent}% /{' '}
                              {plan.outsidePlatformBookingCommissionPercent}%
                            </Typography>
                          </Box>
                        </Stack>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ flexShrink: 0 }}
                        >
                          {catalogueTab === 'private'
                            ? offerCount == null
                              ? t('adminPlans.recipientsUnknown', 'Organizations…')
                              : t('adminPlans.offerCount', '{{count}} offers', {
                                  count: offerCount,
                                })
                            : subscriberCount == null
                              ? t('adminPlans.subscribersUnknown', 'Subscribers…')
                              : t('adminPlans.subscriberCount', '{{count}} subscribed', {
                                  count: subscriberCount,
                                })}
                        </Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, pb: 2 }}>
                      <Stack spacing={2.5}>
                        {catalogueTab === 'standard' ? (
                          <Box>
                            {loadingSubscribers && subscribers == null ? (
                              <Box display="flex" alignItems="center" gap={1.5} py={1}>
                                <CircularProgress size={20} />
                                <Typography variant="body2" color="text.secondary">
                                  {t(
                                    'adminPlans.loadingSubscribers',
                                    'Loading subscribers…',
                                  )}
                                </Typography>
                              </Box>
                            ) : (subscribers || []).length === 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                {t(
                                  'adminPlans.noSubscribers',
                                  'No organizations are subscribed to this plan',
                                )}
                              </Typography>
                            ) : (
                              <Stack spacing={1}>
                                {(subscribers || []).map((subscriber) => {
                                  const orgName =
                                    subscriber.organizationName?.trim() ||
                                    organizations.find(
                                      (org) => org.id === subscriber.organizationId,
                                    )?.name ||
                                    subscriber.organizationId;
                                  return (
                                    <Box
                                      key={subscriber.organizationId}
                                      sx={{
                                        p: 1.25,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1.5,
                                      }}
                                    >
                                      <Typography variant="body1" fontWeight={600}>
                                        {orgName}
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        {statusLabel(subscriber.status)}
                                        {subscriber.currentPeriodEndsAt
                                          ? ` · ${t(
                                              'adminPlans.periodEnds',
                                              'Period ends',
                                            )} ${formatOfferDate(subscriber.currentPeriodEndsAt)}`
                                          : ''}
                                        {subscriber.cancelAtPeriodEnd
                                          ? ` · ${t(
                                              'adminPlans.cancelAtPeriodEnd',
                                              'Cancels at period end',
                                            )}`
                                          : ''}
                                      </Typography>
                                    </Box>
                                  );
                                })}
                              </Stack>
                            )}
                          </Box>
                        ) : null}

                        {catalogueTab === 'private' ? (
                          <Box>
                            {loadingOffers && offers == null ? (
                              <Box display="flex" alignItems="center" gap={1.5} py={1}>
                                <CircularProgress size={20} />
                                <Typography variant="body2" color="text.secondary">
                                  {t('adminPlans.loadingRecipients', 'Loading organizations…')}
                                </Typography>
                              </Box>
                            ) : (offers || []).length === 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                {t(
                                  'adminPlans.noRecipients',
                                  'No organizations have this offer yet',
                                )}
                              </Typography>
                            ) : (
                              <Stack spacing={1}>
                                {(offers || []).map((offer) => {
                                  const revokeKey = `${offer.organizationId}:${offer.plan.priceId}`;
                                  const orgName =
                                    offer.organizationName?.trim() ||
                                    organizations.find((org) => org.id === offer.organizationId)
                                      ?.name ||
                                    offer.organizationId;
                                  return (
                                    <Box
                                      key={offer.id}
                                      sx={{
                                        p: 1.25,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1.5,
                                      }}
                                    >
                                      <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        spacing={1}
                                        alignItems={{ sm: 'center' }}
                                        justifyContent="space-between"
                                      >
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                          <Typography variant="body1" fontWeight={600}>
                                            {orgName}
                                          </Typography>
                                          <Typography variant="body2" color="text.secondary">
                                            {t('organizations.planOffers.expires', 'Expires')}:{' '}
                                            {offer.expiresAt
                                              ? formatOfferDate(offer.expiresAt)
                                              : t(
                                                  'organizations.planOffers.noExpiry',
                                                  'No expiry',
                                                )}
                                            {offer.note ? ` · ${offer.note}` : ''}
                                          </Typography>
                                        </Box>
                                        <IconButton
                                          color="error"
                                          size="small"
                                          aria-label={t(
                                            'organizations.planOffers.revoke',
                                            'Revoke',
                                          )}
                                          disabled={revokingKey === revokeKey}
                                          onClick={() =>
                                            void handleRevoke(
                                              offer.organizationId,
                                              offer.plan.priceId,
                                            )
                                          }
                                        >
                                          {revokingKey === revokeKey ? (
                                            <CircularProgress size={18} />
                                          ) : (
                                            <DeleteOutlineIcon fontSize="small" />
                                          )}
                                        </IconButton>
                                      </Stack>
                                    </Box>
                                  );
                                })}
                              </Stack>
                            )}
                          </Box>
                        ) : null}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Stack>
          )}
        </Box>
      </Card>

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        fullWidth
        maxWidth="sm"
        aria-labelledby="admin-new-plan-offer-title"
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 2.5 },
            overflow: 'hidden',
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle
          sx={(theme) => ({
            px: { xs: 2.25, sm: 3 },
            py: 2.25,
            background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, transparent 100%)`,
          })}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box
              sx={(theme) => ({
                width: 44,
                height: 44,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.16),
                color: theme.palette.primary.main,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                flexShrink: 0,
                mt: 0.15,
              })}
            >
              <LocalOfferOutlinedIcon fontSize="small" />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, pt: 0.35 }}>
              <Typography
                id="admin-new-plan-offer-title"
                variant="h6"
                component="h2"
                sx={{ fontWeight: 700, lineHeight: 1.25, letterSpacing: '-0.01em' }}
              >
                {t('adminPlans.newOffer', 'Make offer')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {t(
                  'adminPlans.grantHint',
                  'Offers are organization-specific. Hosts see unexpired offers beside public plans; an offer is not an active subscription.',
                )}
              </Typography>
            </Box>
            <IconButton
              aria-label={t('common.close', 'Close')}
              onClick={closeDialog}
              disabled={submitting}
              size="small"
              sx={{ mt: 0.25 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2.25, sm: 3 }, pt: 1, pb: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack spacing={2.25} sx={{ pt: 0.5 }}>
              <FormControl fullWidth required>
                <InputLabel id="admin-new-offer-plan-label">
                  {t('organizations.planOffers.privatePlan', 'Private plan')}
                </InputLabel>
                <Select
                  labelId="admin-new-offer-plan-label"
                  label={t('organizations.planOffers.privatePlan', 'Private plan')}
                  value={dialogPlanPriceId}
                  onChange={(event) => setDialogPlanPriceId(String(event.target.value))}
                >
                  {privatePlans.map((plan) => (
                    <MenuItem key={plan.priceId} value={plan.priceId}>
                      {planCodeLabel(plan.code)} ·{' '}
                      {formatPlanPrice(plan.amount, plan.currencyCode)} ·{' '}
                      {orgTypeLabel(plan.organizationType)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Autocomplete
                options={organizations}
                loading={loadingOrgs}
                value={dialogOrg}
                onChange={(_event, value) => setDialogOrg(value)}
                getOptionLabel={(option) => option.name || option.id}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('adminPlans.organization', 'Organization')}
                    required
                  />
                )}
              />
              <DateTimePicker
                label={t('organizations.planOffers.expiresAt', 'Expires at (optional)')}
                ampm={false}
                value={expiresAt}
                minDateTime={new Date()}
                onChange={(value) => setExpiresAt(value)}
                slotProps={{
                  textField: { fullWidth: true },
                  field: { clearable: true },
                  actionBar: { actions: ['clear', 'today', 'accept'] },
                }}
              />
              <TextField
                label={t('organizations.planOffers.note', 'Note (optional)')}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                multiline
                minRows={3}
                fullWidth
              />
            </Stack>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2.25, sm: 3 },
            py: 2,
            gap: 1,
          }}
        >
          <Button onClick={closeDialog} disabled={submitting} color="inherit">
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleGrant()}
            disabled={submitting || !dialogOrg || !dialogPlanPriceId}
            sx={{ borderRadius: 999, px: 2.5, minWidth: 132 }}
          >
            {submitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t('organizations.planOffers.grant', 'Save offer')
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminSubscriptionPlansPage;
